import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Play, Loader2, FileText, ListChecks, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { AdminAuditReports } from "./AdminAuditReports";
import { AdminAuditImprovements } from "./AdminAuditImprovements";

function AuditConfigTab({ onSaved }) {
  const [config, setConfig] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [c, cand] = await Promise.all([
      api.get("/admin/audit/config"),
      api.get("/admin/audit/candidates"),
    ]);
    setConfig(c.data);
    setCandidates(cand.data);
  };
  useEffect(() => { load(); }, []);

  if (!config) return <div className="py-10 text-center text-stone-400">Carregando...</div>;

  const toggleTarget = (id) => {
    const ids = new Set(config.target_user_ids || []);
    ids.has(id) ? ids.delete(id) : ids.add(id);
    setConfig({ ...config, target_user_ids: [...ids] });
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.patch("/admin/audit/config", {
        all_users: config.all_users,
        target_user_ids: config.target_user_ids || [],
        report_phone: config.report_phone,
      });
      setConfig({ ...res.data, resolved_targets: config.resolved_targets });
      toast.success("Configuração da auditoria salva");
      onSaved?.();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 max-w-3xl" data-testid="audit-config-tab">
      <Card className="p-5 space-y-4">
        <div className="rounded-lg border border-[#D4651A]/30 bg-[#D4651A]/5 p-4" data-testid="audit-manual-notice">
          <div className="font-semibold text-[#1B3A2F]">Auditoria 100% manual</div>
          <div className="text-sm text-stone-600 mt-1">
            Não existe mais análise automática nem envio automático no WhatsApp. Você roda pelo botão
            "Rodar auditoria agora" e cada auditoria analisa exatamente o período entre a última
            auditoria válida e o momento do pedido. Na primeira auditoria de um corretor, analisa as últimas 24h.
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-[#1B3A2F]">Auditar todos os corretores</div>
            <div className="text-sm text-stone-500">Se desligado, audita só os corretores selecionados abaixo</div>
          </div>
          <Switch data-testid="audit-all-users-switch" checked={!!config.all_users}
            onCheckedChange={(v) => setConfig({ ...config, all_users: v })} />
        </div>
        <div>
          <div className="font-semibold text-[#1B3A2F] mb-1">WhatsApp que recebe o relatório (envio manual)</div>
          <Input data-testid="audit-report-phone-input" value={config.report_phone || ""}
            onChange={(e) => setConfig({ ...config, report_phone: e.target.value })}
            placeholder="5548984047091" className="max-w-xs" />
        </div>
      </Card>

      {!config.all_users && (
        <Card className="p-5">
          <div className="font-semibold text-[#1B3A2F] mb-1">Corretores auditados</div>
          <div className="text-sm text-stone-500 mb-3">
            Se nenhum for selecionado, a auditoria busca automaticamente pelo nome "{config.target_name_fallback || "jean"}".
            Alvos resolvidos agora: {(config.resolved_targets || []).map((t) => t.name).join(", ") || "nenhum"}
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="audit-candidates-list">
            {candidates.map((u) => (
              <label key={u.id} className="flex items-center gap-3 p-2 rounded hover:bg-stone-50 cursor-pointer">
                <Checkbox data-testid={`audit-candidate-${u.id}`}
                  checked={(config.target_user_ids || []).includes(u.id)}
                  onCheckedChange={() => toggleTarget(u.id)} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#1B3A2F] truncate">{u.name}</div>
                  <div className="text-xs text-stone-400 truncate">{u.email} · {u.whatsapp_messages} msgs WhatsApp</div>
                </div>
              </label>
            ))}
            {candidates.length === 0 && <div className="text-sm text-stone-400">Nenhum corretor encontrado</div>}
          </div>
        </Card>
      )}

      <Button onClick={save} disabled={saving} data-testid="audit-config-save-btn"
        className="bg-[#1B3A2F] hover:bg-[#0F1F1A] text-white">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Salvar configuração
      </Button>
    </div>
  );
}

export default function AdminAudit() {
  const [running, setRunning] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const pollRun = () => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get("/admin/audit/runs/latest");
        const run = res.data;
        if (run.status === "done") {
          stopPolling(); setRunning(false); setRefreshKey((k) => k + 1);
          toast.success("Auditoria concluída! Relatório disponível.");
        } else if (run.status === "failed") {
          stopPolling(); setRunning(false);
          toast.error(`Auditoria falhou: ${run.error || "erro desconhecido"}`);
        }
      } catch { /* keep polling */ }
    }, 4000);
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/admin/audit/runs/latest");
        if (res.data.status === "running") { setRunning(true); pollRun(); }
      } catch { /* ignore */ }
    })();
    return stopPolling;
  }, []);

  const runNow = async () => {
    try {
      setRunning(true);
      await api.post("/admin/audit/run", {});
      toast.info("Auditoria iniciada. Pode levar 1 a 2 minutos.");
      pollRun();
    } catch (e) {
      setRunning(false);
      toast.error(e.response?.data?.detail || "Erro ao iniciar auditoria");
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-6" data-testid="admin-audit-root">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4651A] font-bold mb-1">Qualidade da IA</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1B3A2F]">Auditoria Inteligente</h1>
          <p className="text-stone-500 mt-1 text-sm max-w-xl">
            Análise manual das conversas do WhatsApp com a IA: bugs, alucinações, falhas de CRM e melhorias priorizadas, com evidências. Cada rodada cobre o período desde a última auditoria.
          </p>
        </div>
        <Button onClick={runNow} disabled={running} data-testid="audit-run-now-btn"
          className="bg-[#D4651A] hover:bg-[#B85512] text-white gap-2">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {running ? "Auditando..." : "Rodar auditoria agora"}
        </Button>
      </header>

      <Tabs defaultValue="reports">
        <TabsList className="bg-white border">
          <TabsTrigger value="reports" data-testid="audit-tab-reports" className="gap-2">
            <FileText className="w-4 h-4" /> Relatórios
          </TabsTrigger>
          <TabsTrigger value="improvements" data-testid="audit-tab-improvements" className="gap-2">
            <ListChecks className="w-4 h-4" /> Melhorias
          </TabsTrigger>
          <TabsTrigger value="config" data-testid="audit-tab-config" className="gap-2">
            <Settings2 className="w-4 h-4" /> Configuração
          </TabsTrigger>
        </TabsList>
        <TabsContent value="reports" className="mt-4">
          <AdminAuditReports refreshKey={refreshKey} />
        </TabsContent>
        <TabsContent value="improvements" className="mt-4">
          <AdminAuditImprovements refreshKey={refreshKey} />
        </TabsContent>
        <TabsContent value="config" className="mt-4">
          <AuditConfigTab onSaved={() => setRefreshKey((k) => k + 1)} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
